import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ToggleProps {
  /** Current checked state */
  checked?: boolean;
  /** Default checked state (uncontrolled) */
  defaultChecked?: boolean;
  /** Callback when state changes */
  onChange?: (checked: boolean) => void;
  /** Label text displayed next to the toggle */
  label?: string;
  /** Supporting text displayed under the label */
  description?: React.ReactNode;
  /** Accessible label used when there is no visible label prop */
  'aria-label'?: string;
  /** Id of an element that explains the switch (e.g. why it is locked) */
  'aria-describedby'?: string;
  /** Label position */
  labelPosition?: 'left' | 'right';
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
  /** Custom className for container */
  className?: string;
  /** ID for accessibility */
  id?: string;
  /** Name attribute for form submission */
  name?: string;
}

/**
 * Toggle/Switch component
 */
const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked = false,
      onChange,
      label,
      description,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      labelPosition = 'right',
      disabled = false,
      size = 'default',
      className,
      id,
      name,
    },
    ref
  ) => {
    // Handle controlled vs uncontrolled state
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
    const isControlled = controlledChecked !== undefined;
    const isChecked = isControlled ? controlledChecked : internalChecked;

    const generatedId = React.useId();
    const toggleId = id || generatedId;

    // Size configurations
    const sizeConfig = {
      sm: {
        track: 'w-10 h-5',
        thumb: 'w-4 h-4',
        thumbTranslate: 'translate-x-5',
        gap: 'gap-2',
      },
      default: {
        track: 'w-12 h-6',
        thumb: 'w-5 h-5',
        thumbTranslate: 'translate-x-6',
        gap: 'gap-2.5',
      },
      lg: {
        track: 'w-14 h-7',
        thumb: 'w-6 h-6',
        thumbTranslate: 'translate-x-7',
        gap: 'gap-3',
      },
    };

    const config = sizeConfig[size];

    // Handle toggle
    const handleToggle = () => {
      if (disabled) return;

      const newValue = !isChecked;
      if (!isControlled) {
        setInternalChecked(newValue);
      }
      onChange?.(newValue);
    };

    // Handle keyboard
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    };

    const toggleElement = (
      <button
        ref={ref}
        id={toggleId}
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-label={!label ? ariaLabel : undefined}
        aria-labelledby={label ? `${toggleId}-label` : undefined}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer items-center rounded-full',
          'transition-colors duration-200 ease-in-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2 focus-visible:ring-offset-clovie-surface',
          config.track,
          isChecked ? 'bg-clovie-brand' : 'bg-clovie-border-strong',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {/* Hidden input for form submission */}
        {name && <input type="hidden" name={name} value={isChecked ? 'true' : 'false'} />}

        {/* Thumb */}
        <span
          className={cn(
            'pointer-events-none inline-block rounded-full bg-clovie-surface shadow-soft',
            'ring-0 transition-transform duration-200 ease-in-out',
            config.thumb,
            isChecked ? config.thumbTranslate : 'translate-x-0.5'
          )}
        />
      </button>
    );

    // If no label, just return the toggle
    if (!label) {
      return toggleElement;
    }

    if (description) {
      return (
        <div
          className={cn(
            'inline-flex items-start',
            config.gap,
            labelPosition === 'left' && 'flex-row-reverse',
            className
          )}
        >
          {toggleElement}
          <div className="min-w-0">
            <label
              id={`${toggleId}-label`}
              htmlFor={toggleId}
              className={cn(
                'block cursor-pointer select-none text-[15px] font-medium text-clovie-text',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {label}
            </label>
            <p className="mt-1 text-sm leading-6 text-clovie-muted">{description}</p>
          </div>
        </div>
      );
    }

    // With label
    return (
      <div
        className={cn(
          'inline-flex items-center',
          config.gap,
          labelPosition === 'left' && 'flex-row-reverse',
          className
        )}
      >
        {toggleElement}
        <label
          id={`${toggleId}-label`}
          htmlFor={toggleId}
          className={cn(
            'cursor-pointer select-none text-[15px] font-medium text-clovie-text',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </label>
      </div>
    );
  }
);
Toggle.displayName = 'Toggle';

export { Toggle };
